import { useState, useRef, useCallback } from 'react';
import type { DragEvent, ChangeEvent } from 'react';
import { useAppStore } from '@/store';
import { parseCGATS, buildMeasuredPatches } from '@/engine/cgats';
import { cleanMeasurements } from '@/engine/data-quality';
import { computeDiagnostics } from '@/engine/diagnostics';
import { DEFAULT_CLEANING_OPTIONS } from '@/types';
import type { ParsedCGATS } from '@/types';

// =============================================================================
// FileDropZone
// =============================================================================

function FileDropZone({ onFile, disabled }: { onFile: (f: File) => void; disabled?: boolean }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  }, [onFile]);

  return (
    <div
      onClick={() => !disabled && inputRef.current?.click()}
      onDrop={handleDrop}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      className={[
        'h-[200px] border-2 border-dashed rounded-xl flex flex-col items-center justify-center',
        'transition-colors select-none',
        disabled ? 'cursor-default opacity-50' : 'cursor-pointer',
        dragging
          ? 'border-sky-400 bg-sky-900/20 text-sky-300'
          : 'border-slate-600 hover:border-slate-500 text-slate-400',
      ].join(' ')}
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 mb-2 opacity-60"
        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M9 12h6m-3-3v6M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M16 8l-4-4-4 4" />
      </svg>
      <p className="text-sm">Drop your measured CGATS file here, or click</p>
      <p className="text-xs mt-1 text-slate-500">Accepts: .txt · .cgats</p>
      <input
        ref={inputRef}
        type="file"
        accept=".txt,.cgats,.TXT,.CGATS"
        className="hidden"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}

// =============================================================================
// CGATSPreviewTable
// =============================================================================

function CGATSPreviewTable({ cgats }: { cgats: ParsedCGATS }) {
  const PREVIEW_ROWS = 10;
  const rows   = cgats.rows.slice(0, PREVIEW_ROWS);
  const fields = cgats.fields;

  return (
    <div className="overflow-auto rounded-lg border border-slate-700 max-h-64">
      <table className="text-xs font-mono w-full border-collapse">
        <thead className="sticky top-0 bg-slate-800">
          <tr>
            {fields.map(f => (
              <th key={f} className="px-3 py-1.5 text-left text-slate-400 whitespace-nowrap border-b border-slate-700">
                {f}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-slate-800/80 hover:bg-slate-800/40">
              {fields.map(f => (
                <td key={f} className="px-3 py-1 text-slate-300 whitespace-nowrap">
                  {row[f] ?? '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {cgats.rows.length > PREVIEW_ROWS && (
        <p className="text-xs text-slate-500 px-3 py-1.5 bg-slate-800/60 border-t border-slate-700">
          … and {cgats.rows.length - PREVIEW_ROWS} more rows
        </p>
      )}
    </div>
  );
}

// =============================================================================
// FieldChipList
// =============================================================================

const LAB_FIELDS  = new Set(['LAB_L', 'LAB_A', 'LAB_B']);
const RGB_FIELDS  = new Set(['RGB_R', 'RGB_G', 'RGB_B']);

function FieldChipList({ fields }: { fields: string[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {fields.map(f => {
        const isLab = LAB_FIELDS.has(f);
        const isRgb = RGB_FIELDS.has(f);
        return (
          <span key={f} className={[
            'px-2 py-0.5 rounded text-xs font-mono',
            isLab ? 'bg-emerald-900/60 text-emerald-300 ring-1 ring-emerald-700'
            : isRgb ? 'bg-sky-900/60 text-sky-300 ring-1 ring-sky-700'
            : 'bg-slate-800 text-slate-400',
          ].join(' ')}>
            {f}
          </span>
        );
      })}
    </div>
  );
}

// =============================================================================
// ErrorBanner / SuccessBar helpers
// =============================================================================

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-red-500 bg-red-900/30 px-4 py-3 text-sm text-red-300 font-mono">
      ✕ {message}
    </div>
  );
}

function InfoRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <span className={highlight ? 'text-sky-300 font-medium' : 'text-slate-400'}>
      {label}: <span className="font-mono">{value}</span>
    </span>
  );
}

// =============================================================================
// ImportView
// =============================================================================

export default function ImportView() {
  const [error,        setError]        = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const cgats          = useAppStore(s => s.session.rawCGATS);
  const patches        = useAppStore(s => s.session.patches);
  const setCGATS       = useAppStore(s => s.setCGATS);
  const setRawPatches  = useAppStore(s => s.setRawPatches);
  const setCleanResult = useAppStore(s => s.setCleaningResult);
  const setDiagnostics = useAppStore(s => s.setDiagnostics);
  const setView        = useAppStore(s => s.setView);

  const handleFile = async (file: File) => {
    setError(null);
    setIsProcessing(true);
    try {
      const text = await file.text();

      const parseResult = parseCGATS(text, file.name);
      if (!parseResult.ok) {
        setError(parseResult.error);
        return;
      }

      const patchResult = buildMeasuredPatches(parseResult.value);
      if (!patchResult.ok) {
        setError(patchResult.error);
        return;
      }

      const cleanResult = cleanMeasurements(patchResult.value, DEFAULT_CLEANING_OPTIONS);

      setCGATS(parseResult.value);
      setRawPatches(patchResult.value);
      setCleanResult(cleanResult.patches, cleanResult.report);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRunDiagnostics = () => {
    if (!patches?.length) return;
    // Deep-copy patches — Zustand+Immer freezes state; diagnostics mutates .flags in-place
    const patchesCopy = patches.map(p => ({ ...p, flags: { ...p.flags } }));
    const report = computeDiagnostics(patchesCopy);
    setDiagnostics(report);
    setView('diagnose');
  };

  const patchCount  = patches?.length ?? 0;
  const rowCount    = cgats?.rows.length ?? 0;
  const hasLab      = cgats?.fields.some(f => LAB_FIELDS.has(f)) ?? false;

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 space-y-5">

      {/* Title */}
      <div>
        <h1 className="text-lg font-semibold text-slate-100">Import Measurement File</h1>
        <p className="text-sm text-slate-400 mt-0.5">
          Load a CGATS file containing RGB + Lab measurement data.
        </p>
      </div>

      {/* Drop zone */}
      <FileDropZone onFile={handleFile} disabled={isProcessing} />

      {/* Processing spinner */}
      {isProcessing && (
        <p className="text-sm text-sky-400 font-mono animate-pulse text-center">
          Parsing…
        </p>
      )}

      {/* Error */}
      {error && <ErrorBanner message={error} />}

      {/* Loaded state */}
      {cgats && patches && !isProcessing && (
        <div className="space-y-4">

          {/* Summary bar */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-slate-800/60
                          border border-slate-700 px-4 py-2.5 text-sm">
            <span className="text-emerald-400 font-medium">✓ {cgats.originator || 'file loaded'}</span>
            <InfoRow label="rows" value={String(rowCount)} />
            <InfoRow label="patches" value={String(patchCount)} highlight />
            <InfoRow label="fields" value={String(cgats.fields.length)} />
            {!hasLab && (
              <span className="text-red-400 text-xs">⚠ Lab fields missing</span>
            )}
          </div>

          {/* Missing-Lab warning */}
          {!hasLab && (
            <ErrorBanner message="Missing LAB_L / LAB_A / LAB_B fields — please import a measured file." />
          )}

          {/* Preview table */}
          <div>
            <p className="text-xs text-slate-500 mb-1.5 uppercase tracking-wide">
              Preview (first 10 rows)
            </p>
            <CGATSPreviewTable cgats={cgats} />
          </div>

          {/* Field chips */}
          <div>
            <p className="text-xs text-slate-500 mb-1.5 uppercase tracking-wide">Fields</p>
            <FieldChipList fields={cgats.fields} />
          </div>

          {/* Run button */}
          {hasLab && (
            <div className="pt-1">
              <button
                onClick={handleRunDiagnostics}
                className="px-5 py-2 rounded-lg bg-sky-500 hover:bg-sky-400 text-slate-900
                           font-semibold text-sm transition-colors"
              >
                → Run Diagnostics
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
